import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => setOpen(true), []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeQuoteForm();
      };
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, closeQuoteForm]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeQuoteForm(); }}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="quote-form-title"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden bg-card p-8"
            >
              <button
                onClick={closeQuoteForm}
                className="absolute top-3 right-3 z-10 p-2 rounded-full bg-muted hover:bg-muted/80 text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              <h3 id="quote-form-title" className="font-heading text-2xl text-foreground mb-2 font-bold">Get Your Free Estimate</h3>
              <p className="text-muted-foreground text-sm mb-4">Tell us about your space and we'll get right back to you.</p>

              <div className="max-h-[70vh] overflow-y-auto scrollbar-slim">
                <iframe
                  src="https://link.systemic-digital.net/widget/form/3eMUXdWvrrgfTubLLFWN"
                  style={{ width: "100%", height: "670px", border: "none", borderRadius: "6px" }}
                  id="inline-3eMUXdWvrrgfTubLLFWN-modal"
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="JR Pro Cleaning Services Form"
                  data-height="670"
                  data-layout-iframe-id="inline-3eMUXdWvrrgfTubLLFWN-modal"
                  data-form-id="3eMUXdWvrrgfTubLLFWN"
                  title="JR Pro Cleaning Services Form"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuoteFormContext.Provider>
  );
};
